import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { get } from 'lodash';
import { ComponentPropsProvider, SiteHeaderLayout, ExplorerEntityDetailsLayout, ExplorerEntityDetailsLayoutDefaultProps, ExplorerEntityDetailsLayoutProps, SliceLayoutBoundary, SvgCanvasViewBoxDefinition, MetaSectionEntity, PersonCell, PersonViewLayout, PersonMetaLayout, ExplorerTabs, PersonBanner, ActiveEntityProvider } from '@shapeable/ui';
import { ExplorerProvider } from '../providers/explorer-provider';
import { PersonWithNavParent, transformPeopleEvents } from '../../utils/transforms';
import { RadarChart, useRadar } from '@shapeable/gesda-ui';
const cls = classNames('person-explorer-layout');

// -------- Types -------->

// -------- Props -------->

export type PersonExplorerLayoutProps = ExplorerEntityDetailsLayoutProps & { 
  entity: PersonWithNavParent;
};

export const PersonExplorerLayoutDefaultProps: Omit<PersonExplorerLayoutProps, 'entity'> = {
  ...ExplorerEntityDetailsLayoutDefaultProps,
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
  
  `,
});

const LayoutStyles = breakpoints({
  base: css`
    padding-top: ${theme.UNIT(4)};
  `,
});

const HeaderStyles = breakpoints({
  base: css`
    
  `,
});

const RadarChartStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(4)};
    opacity: 0.4;
    filter: saturate(0.4);
  `,
});

const TabsStyles = breakpoints({
  base: css`
    
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Header: styled(SiteHeaderLayout)`${HeaderStyles}`,
    Tabs: styled(ExplorerTabs)`${TabsStyles}`,
    Layout: styled(ExplorerEntityDetailsLayout)`${LayoutStyles}`,
      Radar: styled(RadarChart)`${RadarChartStyles}`,
};

export const PersonExplorerLayout: Shapeable.FC<PersonExplorerLayoutProps> = (props) => {
  const { className, children, entity } = props;

  const transformedEntity = useMemo(
    () => transformPeopleEvents(entity),
    [entity],
  );

  const topicExpertise = (transformedEntity.topicExpertise || []).map((topicExpertise) => {
    const firstTopic = topicExpertise?.topics?.[0];

    return {
      ...topicExpertise,
      topic: firstTopic,
      path: firstTopic?.path,
    };
  });
  
  const metaItems: MetaSectionEntity[] = useMemo(() => {
    const sections = [
      { items: topicExpertise.filter(ex => ex.levelOfExpertise?.slug === 'anticipation-committee-chair'), title: 'Anticipation Committee Chair', label: 'Pulse of Science' },
      { items: topicExpertise.filter(ex => ex.levelOfExpertise?.slug === 'anticipation-committee-member'), title: 'Anticipation Committee member', label: 'Pulse of Science' },
    ];

    return sections.filter(section => section.items.length > 0);
  }, [topicExpertise]);
  
  const radar = useRadar();

  let initialViewBox: SvgCanvasViewBoxDefinition;
  const trend = get(topicExpertise, '[0].topic.trend');
  
  if (trend) {
    initialViewBox = radar.viewboxPresets?.[trend?.slug];
  }

  return (
    <ExplorerProvider>
    <ActiveEntityProvider value={entity.parent}>
    <ComponentPropsProvider value={{ 
      PersonCell: {
        size: 'banner',
      },
      PersonViewLayout: {
        relatedContentLabel: 'Related Radar Content',
      },
      PersonMetaLayout: {
        metaItems: metaItems,
        displayOrgs: false,
      }
    }}>
    <My.Container>
      <My.Header />
      <SliceLayoutBoundary boundary='none' >
        <My.Layout 
          ignoreViews
          entity={transformedEntity}
          isAsideSticky={false}
          showAsideHeader={false}
          
          className={cls.name(className)}
          banner={<PersonBanner entity={transformedEntity} />}
          content={<PersonViewLayout entity={transformedEntity} />}
          metaHead={<PersonMetaLayout entity={transformedEntity} />}
          dataViz={
            !!initialViewBox &&
            <My.Radar 
              fadeSize={!!initialViewBox && 44} 
              initialViewBox={initialViewBox} 
              isInteractive={!initialViewBox}
              initialAngle={135}
            />
          }
        /> 
      </SliceLayoutBoundary>
    </My.Container>
    </ComponentPropsProvider>
    </ActiveEntityProvider>
    </ExplorerProvider>
  )
};

PersonExplorerLayout.defaultProps = PersonExplorerLayoutDefaultProps;
PersonExplorerLayout.cls = cls;